import { Link } from 'react-router-dom';
import type { Film } from '@/types/film';
import FilmCard from '@/components/feature/FilmCard';
import { categoryList } from '@/mocks/categories';

interface SearchResultsProps {
  keyword: string;
  results: Film[];
  suggestions: string[];
  onSearch: (value: string) => void;
}

export default function SearchResults({ keyword, results, suggestions, onSearch }: SearchResultsProps) {
  const hasKeyword = keyword.trim().length > 0;

  return (
    <div className="w-full px-4 md:px-8 pt-10 pb-16">
      <header className="max-w-3xl">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">搜索影视作品</h1>
        <p className="mt-2 text-sm text-foreground-500">
          支持模糊匹配影视标题、题材、地区与标签，输入关键词即可快速定位想看的作品。
        </p>
      </header>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <span className="text-xs text-foreground-400">热门搜索</span>
        {suggestions.map((word) => (
          <button
            key={word}
            type="button"
            onClick={() => onSearch(word)}
            className={`px-2.5 py-1 rounded-md text-xs whitespace-nowrap transition-colors cursor-pointer ${
              keyword === word
                ? 'bg-accent-500 text-primary-950 font-medium'
                : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            {word}
          </button>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-background-200">
        <p className="text-sm text-foreground-700">
          {hasKeyword ? (
            <>
              关于 <span className="font-semibold text-foreground-950">“{keyword}”</span> 的搜索结果
            </>
          ) : (
            '尚未输入关键词'
          )}
        </p>
        {hasKeyword && (
          <span className="px-3 py-1 rounded-md bg-accent-100 text-accent-900 text-xs whitespace-nowrap">
            共找到 {results.length} 部作品
          </span>
        )}
      </div>

      {!hasKeyword ? (
        <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center px-4">
          <span className="w-14 h-14 mx-auto rounded-full bg-background-50 text-foreground-400 flex items-center justify-center">
            <i className="ri-search-line text-2xl" />
          </span>
          <p className="mt-4 text-sm font-medium text-foreground-950">输入关键词开始搜索</p>
          <p className="mt-2 text-xs text-foreground-500">可以试试搜索「悬疑」「2025」或直接输入影视名称</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            {categoryList.map((item) => (
              <Link
                key={item.key}
                to={item.path}
                className="h-9 px-4 rounded-md bg-background-50 border border-background-300 text-xs text-foreground-700 flex items-center gap-1.5 whitespace-nowrap hover:border-accent-400 transition-colors"
              >
                <i className={item.icon} />
                浏览{item.label}
              </Link>
            ))}
          </div>
        </div>
      ) : results.length === 0 ? (
        <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center px-4">
          <span className="w-14 h-14 mx-auto rounded-full bg-background-50 text-foreground-400 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-2xl" />
          </span>
          <p className="mt-4 text-sm font-medium text-foreground-950">没有找到与「{keyword}」相关的影视作品</p>
          <p className="mt-2 text-xs text-foreground-500">换个关键词试试，或者按分类浏览全站内容</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <Link
              to="/"
              className="h-9 px-4 rounded-md bg-primary-500 text-background-50 text-xs flex items-center gap-1.5 whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
            {categoryList.map((item) => (
              <Link
                key={item.key}
                to={item.path}
                className="h-9 px-4 rounded-md bg-background-50 border border-background-300 text-xs text-foreground-700 flex items-center gap-1.5 whitespace-nowrap hover:border-accent-400 transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-x-4 gap-y-8">
          {results.map((film, index) => (
            <FilmCard key={film.id} film={film} index={index} />
          ))}
        </div>
      )}
    </div>
  );
}